export const PURGE_TEXTURES = ['MeshFxWaterMagicArea3SustainTex0', 'PurchasedFSmoke13'] as const;
export type PurgeVariant = 'white-fire' | 'flame' | 'restoration' | 'curse' | 'water';
export const PURGE_VARIANTS: readonly PurgeVariant[] = ['white-fire','flame','restoration','curse','water'];
export const PURGE_ASSETS = [
  { key: PURGE_TEXTURES[0], sha256: '1b7c109ab7201b83b8e8fdba3a8be2cf24dcd1e87cae4d6b31dffb9544758eb4', bytes: 181458 },
  { key: PURGE_TEXTURES[1], sha256: 'c2eadef2b5f07b5bbb8da95adfdabb3bff985623290c6b3718a9503b618e3152', bytes: 637834 },
];
export const PURGE_FIRE_ASSET = { key: 'BigPackFireHit', sha256: '79d9274e824ff50104f92d32f37f87f8eac4cc3ed014194ab8e002c225e1b547', bytes: 193296 };
export function purgeTextures(variant?: PurgeVariant): readonly string[] {
  return typeof variant==='string'&&Object.prototype.hasOwnProperty.call(VARIANT_ART,variant) ? VARIANT_ART[variant].textures : PURGE_TEXTURES;
}
export const FIRE_TEXTURES = [...PURGE_TEXTURES, PURGE_FIRE_ASSET.key];
/** Headless family art metadata; the renderer consumes the same texture choices. */
export const HOLY = { textures: PURGE_TEXTURES as readonly string[], shape: 0, matter: 0, rim: [1,.74,.34], core: [1,.98,.89], tint: [0,0,0],
  darkRim: [.24,.10,.33], darkCore: [.055,.025,.085],
  casterDarkRim: [.24,.10,.33], casterDarkCore: [.055,.025,.085], lead: .22, wake: .18, matterSize: .27,
  matterStep: .028, matterAspect: 1, burstSpeed: .9, burstLift: 0, gravity: 2.4, burstAspect: 1,
  flashSize: .46, flashLife: .20, flashPower: .9, streaks: 14, streakPower: .8,
  glimmerSize: .036, glimmerPower: .28, leadFloor: 14 };
export const VARIANT_ART: Record<PurgeVariant, typeof HOLY> = {
  'white-fire': { ...HOLY, textures: FIRE_TEXTURES, leadFloor: 22, shape: 1, matter: 1, rim: [1,.84,.48], core: [1,1,.96],
    darkRim: [.28,.28,.27], darkCore: [.055,.06,.055], matterSize: .16, matterStep: .014,
    matterAspect: .75, burstLift: .45, gravity: -.6, burstSpeed: .65, flashLife: .24, streaks: 8 },
  flame: { ...HOLY, textures: FIRE_TEXTURES, leadFloor: 22, shape: 1, rim: [1,.31,.025], core: [1,.92,.48],
    darkRim: [.44,.50,.055], darkCore: [.13,.24,.025], matterSize: .25,
    flashLife: .24, streaks: 8 },
  restoration: { ...HOLY, leadFloor: 18, shape: 2, rim: [1,.85,.51], core: [1,.99,.89], tint: [.69,.93,.59],
    darkRim: [.30,.36,.25], darkCore: [.105,.155,.10], matterSize: .24,
    burstSpeed: .42, gravity: .8, flashSize: .52, flashLife: .30, flashPower: .65,
    streaks: 4, streakPower: .22, glimmerSize: .14, glimmerPower: .35 },
  curse: { ...HOLY, leadFloor: 18, shape: 3, matter: 2, rim: [.61,.77,1], core: [.96,.98,1],
    darkRim: [.31,.08,.43], darkCore: [.075,.022,.13], matterSize: .25,
    matterAspect: .13, flashPower: .75, streaks: 10 },
  water: { ...HOLY, leadFloor: 22, shape: 4, rim: [.18,.60,1], core: [.82,.97,1],
    darkRim: [.29,.43,.095], darkCore: [.075,.17,.045],
    casterDarkRim: [.15,.18,.17], casterDarkCore: [.035,.055,.045], matterSize: .25,
    burstSpeed: .7, gravity: 3.8, burstAspect: 1.4, flashSize: .35,
    flashPower: .42, streaks: 0 },
};

export function validPurgeAssets(assets: Record<string, any>, variant?: PurgeVariant): boolean {
  const fire = purgeTextures(variant).includes(PURGE_FIRE_ASSET.key);
  return (fire ? [...PURGE_ASSETS, PURGE_FIRE_ASSET] : PURGE_ASSETS).every(a => assets[a.key]?.sha256 === a.sha256 && assets[a.key]?.bytes === a.bytes
    && assets[a.key]?.url === `/assets/vfx/magic-v1/${a.sha256}.webp`)
    && assets.PurchasedFSmoke13?.grid === 10 && assets.PurchasedFSmoke13?.frames === 100
    && (!fire || assets.BigPackFireHit?.grid === 6 && assets.BigPackFireHit?.frames === 36);
}
/** One family enum, mutually exclusive with legacy warmth; body dimensions never enter a recipe. */
export function validPurgeRecipe(r: Record<string, any>): boolean {
  return Object.keys(r).every(k => ['schema','kind','contactStyle','texture','blend','color','duration','scale','anchor','castTiming','flareWarmth','purgeVariant',...(r.contactStyle==='family'?['family']:[])].includes(k))
    && r.schema === 'ft.vfx.recipe.v1' && r.kind === 'contact' && (r.contactStyle === 'family' ? r.family === 'cleanse' : r.contactStyle === 'purchased-purge-spiral')
    && r.texture === PURGE_TEXTURES[0] && r.blend === 'alpha' && r.color === '#fff4d2'
    && typeof r.duration === 'number' && Number.isFinite(r.duration) && r.duration >= 1.6 && r.duration <= 2
    && r.scale === 1 && (r.anchor === 'target' || r.anchor === 'caster' && r.purgeVariant === 'water') && r.castTiming === 'gesture-release-v1'
    && (!Object.prototype.hasOwnProperty.call(r, 'flareWarmth') || r.flareWarmth === 0 || r.flareWarmth === 1)
    && (!Object.prototype.hasOwnProperty.call(r, 'purgeVariant') || PURGE_VARIANTS.includes(r.purgeVariant)
      && !Object.prototype.hasOwnProperty.call(r, 'flareWarmth'));
}

